<!--
QuickAdd is for adding an item to a project/cart with a selected substrate and size.

- Logged in users get a project selector with their projects and workspaces.
- Anonymous users are prompted for their email address to create a project/cart, on first add.

- The substrate and size options come from an item's metadata when available,
  or from querying the availablesizes API with the item's aspect ratio.
- Selected options can be passed in, which will be mutated when the selections change,
  so selections can be preserved when the item changes.
- When allowed, custom sizes can be entered by typing in the size field.
  - Type both dimensions to specify a size e.g. 8x6
  - Type one dimension to generate dynamic options for proportionate sizes
    e.g. With a 4x3 image, typing 8 will show options for 8x6 and 10.67x8
  - Recent custom sizes are saved to user preferences for persistence across sessions.
    - Saved dynamic sizes will be adaptive to the aspect ratio of the current item.
-->

<template>
  <div>
    <div class="search-option">
      <div class="title">
        <label for="quick-add-substrate" class="m-0">Substrate</label>
      </div>
      <Multiselect
        id="quick-add-substrate"
        v-model="selected.substrate"
        track-by="value"
        label="label"
        placeholder="Select"
        :options="substrateOptions"
        :loading="substratesLoading"
        :show-labels="false"
        :preselect-first="false"
        :allow-empty="false"
        @input="onSelectSubstrate"
      ></Multiselect>
    </div>
    <div class="search-option">
      <div class="title">
        <label for="quick-add-size" class="m-0">Size in inches</label>
      </div>
      <Multiselect
        id="quick-add-size"
        v-model="selected.size"
        track-by="label"
        :placeholder="customSizesAllowed ? 'Select or type custom size (e.g. 20x30)' : 'Select size'"
        :options="sizeOptionsFiltered"
        :loading="sizesLoading"
        label="label"
        :preselect-first="false"
        :show-labels="false"
        :allow-empty="false"
        :taggable="customSizesAllowed"
        :tag-placeholder="customSizesAllowed ? 'Press enter to add custom size' : ''"
        :internal-search="false"
        @tag="addCustomSize"
        @search-change="onSizeSearchChange"
        @select="onSizeSelect"
      ></Multiselect>
    </div>
    <div class="search-option" v-show="profile">
      <div class="title">
        <label for="quick-add-workspace" class="m-0">Project</label>
      </div>
      <QuickAddWorkspace
        id="quick-add-workspace"
        ref="workspaceInput"
        v-model="selected.workspace"
        :profile="profile"
        :busy.sync="workspacesLoading"
      />
    </div>
    <div class="search-option d-flex gap-2" v-if="isStaff">
      <div>
        <div class="title">
          <label for="quick-add-quantity" class="m-0">Quantity</label>
        </div>
        <b-form-spinbutton
          id="quick-add-quantity"
          v-model="selected.quantity"
        />
      </div>
      <div class="flex-fill">
        <div class="title">
          <label for="quick-add-location" class="m-0">Location</label>
        </div>
        <b-form-input
          id="quick-add-location"
          v-model="selected.location"
          placeholder="Enter location"
          trim
        />
      </div>
    </div>
    <div class="mt-3">
      <LoadingButton
        variant="primary"
        class="mr-2 mb-2"
        @click="add(item)"
        :busy="busy === 'add'"
        :disabled="disabled"
      >Add {{profile ? 'to Project' : 'to Cart'}}</LoadingButton>
      <button
        type="button"
        class="btn btn-link mb-2"
        :disabled="disabled"
        @click="onCustomizeClick($event, item)"
      >Customize</button>
      <b-spinner small
        class="ml-2 align-baseline"
        v-show="!profile && workspacesLoading"
      />
    </div>
    <QuickModal
      id="modal-anon"
      title="Enter your email to save your cart"
      ref="modalAnon"
      centered
    >
      <AnonUserForm
        ref="formAnon"
        @submit="$refs && $refs.modalAnon.hide('ok')"
      ><span/></AnonUserForm>
      <div class="form-row mt-n3">
        <div class="col offset-lg-3 offset-sm-4 text-muted">
          <small>If you already have an account, <router-link :to="{name: 'accounts-login', query: {redirect: $route.fullPath}}">sign in</router-link> instead.</small>
        </div>
      </div>
    </QuickModal>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapState, mapActions } from 'vuex'
import { startCase, memoize, uniqBy, round } from 'lodash'

import QuickAddWorkspace from '@/components/QuickAddWorkspace.vue'
import AnonUserForm from '@/components/AnonUserForm.vue'
import QuickModal from '@/components/QuickModal.vue'
import { API_URL, UI_URL } from '@/services/constants.js'
import { http, getAppApi } from '@/services/http.js'
import { getSrc } from '@/services/media.js'

export default {
  name: 'QuickAdd',
  components: {
    Multiselect,
    QuickAddWorkspace,
    AnonUserForm,
    QuickModal,
  },
  props: {
    item: Object,
    selected: {
      type: Object,
      default: () => ({
        substrate: null,
        size: null,
        workspace: null,
        quantity: 1,
        location: '',
      })
    },
  },
  data() {
    return {
      substrates: [
      ],
      sizes: {
      },
      substratesLoading: false,
      sizesLoading: false,
      workspacesLoading: false,
      busy: false,
      appApi: getAppApi(),
      // This component interacts with external state through this.selected and
      // this.$SmartProgress. this.foreground supports graceful interaction with that state.
      foreground: true,
      dynamicSizeOptions: [], // Dynamic size options based on typed numbers
      sizeSearchQuery: '', // Current search query for size filtering
    }
  },
  computed: {
    ...mapState('user', {
      user: 'user',
      profile: 'profile',
    }),
    isStaff() {
      return this.$store.getters['user/isStaff']
    },
    'customSizes'() {
      return this.$store.state.preferences?.customSizes || []
    },
    cart: {
      get() {
        return this.$store.state.cart.cart
      },
      set(val) {
        this.$store.commit('cart/setCart', val)
      }
    },
    substrateOptions() {
      return this.substrates?.map(i => ({
        label: startCase(i),
        value: i,
        // $isDisabled: !this.sizes[i]?.length
      }))
    },
    sizeOptions() {
      return this.getSizes(this.selected.substrate)
    },
    sizeOptionsFiltered() {
      // Normalize the search query and use it for filtering
      const { normalized } = parseDimensionString(this.sizeSearchQuery);

      return this.sizeOptions.filter(size =>
        size.label?.toLowerCase().includes(normalized.toLowerCase())
      );
    },
    standardSizes() {
      // Returns only the standard sizes for the selected substrate, without custom sizes
      return this.getStandardSizes(this.selected.substrate)
    },
    customSizesAllowed() {
      return this.getCustomSizesAllowed(this.selected.substrate)
    },
    designStudioQuery() {
      const query = {
        image_id: this.item?.id,
        substrate: this.selected?.substrate?.value,
        width: this.selected?.size?.value?.w,
        height: this.selected?.size?.value?.h,
        quantity: this.selected?.quantity,
        location: this.selected?.location,
        workspace_id: this.selected?.workspace?.id,
      }

      return Object.fromEntries(
        Object.entries(query).filter(([, i]) => i)
      )
    },
    disabled() {
      return Boolean(this.busy)
          || !(this.selected.size && this.selected.substrate)
          || (!this.selected.workspace && this.workspacesLoading)
          || this.substratesLoading
          || this.sizesLoading
    },
  },
  watch: {
    item: {
      handler: 'onChange',
      immediate: true,
    }
  },
  created() {
    // component is no longer foreground if route change is initiated
    this.deregisterRouterGuard = this.$router.beforeEach((to, from, next) => {
      this.foreground = false
      next()
    })
  },
  destroyed() {
    this.foreground = false
    if (this.busy === 'open') {
      this.$SmartProgress.finish()
    }
    this.deregisterRouterGuard?.()
  },
  methods: {
    ...mapActions('user', [
      'fetchProfile',
    ]),
    getAnonEmail() {
      return this.$refs?.modalAnon.getValue(() => this.$refs?.formAnon.validate())
        .then(data => data.email)
    },
    async onChange(item) {
      this.$refs.workspaceInput && this.$refs.workspaceInput.onChange()
      await this.loadSizes(item)
      if (!this.foreground) { return } // don't interfere with other instances
      this.updateSelection()
    },
    updateSelection() {
      this.selected.substrate = this.getSubstrate(this.selected.substrate)
      this.selected.size = this.getSize(this.selected.size);
    },
    onSelectSubstrate() {
      this.selected.size = this.getSize(this.selected.size)
    },
    resetChoices() {
      this.substrates = []
      this.sizes = {}
      this.dynamicSizeOptions = []
    },
    async loadSizes(item, onUpdate) {
      if (!item || !item.max_width) {
        this.resetChoices()
        return
      }

      this.substrates = item.substrates || []
      this.sizes = item.substrate_sizes || {}
      onUpdate?.()
      if (!item.substrates || !item.substrate_sizes) {
        this.substratesLoading = !item.substrates
        this.sizesLoading = !item.substrate_sizes
        return await sizesApiCached(item.max_width, item.max_height).then((response) => {
          if (!item.substrates) {
            this.substrates = response.data.substrates;
          }
          if (!item.substrate_sizes) {
            this.sizes = response.data.sizes;
          }
          onUpdate?.()
        }).catch(this.resetChoices)
        .finally(() => {
          this.substratesLoading = false
          this.sizesLoading = false
        })
      }
    },
    getCustomSizesAllowed(substrate) {
      // Only allow custom sizes for logged in users
      if (!this.profile) {
        return false
      }

      // Don't allow custom sizes on items that have predefined substrate_sizes
      if (this.item?.substrate_sizes) {
        return false
      }

      // Don't allow custom sizes for mural and window film substrates
      substrate = substrate?.value?.toLowerCase()
      return substrate !== 'mural' && substrate !== 'window film'
    },
    getStandardSizes(substrate) {
      // Returns only the standard sizes for the given substrate, without custom sizes
      const result = this.sizes[substrate?.value]
      if (!result) { return [] }
      return result.map((s) => {
        return (s.width && s.height)
          ? {
            label: `${s.width} x ${s.height}`.replace(/\.00/g, ''),
            value: { w: s.width, h: s.height },
          }
          : { label: 'Custom', value: { w: 999999.0, h: 999999.0 } }
      })
    },
    getSizes(substrate){
      // returns the list of sizes for a given substrate
      const standardSizes = this.getStandardSizes(substrate)
      if (!this.getCustomSizesAllowed(substrate)) {
        return standardSizes
      }

      // Process custom sizes, regenerating adaptive ones based on current item's aspect ratio
      const customSizes = this.customSizes.map(cs => {
        // If it has userInput and aspect ratio is available, potentially regenerate
        if (cs.userInput && this.item?.max_width && this.item?.max_height) {
          const aspectRatio = this.item.max_width / this.item.max_height;

          // If either dimension is null, it's adaptive and needs regeneration
          if (isAdaptive(cs.userInput)) {
            return createSizeOption(cs.userInput, aspectRatio);
          }
        }
        return cs; // Return non-adaptive sizes as-is
      }).filter(cs => cs.value?.w && cs.value?.h); // Filter out invalid sizes

      const sizes = [...standardSizes, ...customSizes, ...this.dynamicSizeOptions]

      sizes.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });

      const uniqueSizes = uniqBy(sizes, 'label')

      return uniqueSizes
    },
    getSize(size) {
      // returns a size object from list of available sizes
      return this.findSize(size)
        || this.standardSizes[2]
        || this.standardSizes[0]
        || this.sizeOptions[0]
    },
    findSize(size) {
      // returns matching size object if it exists in the list of available sizes
      // we need the exact object because the selection UI compares by reference
      if (!size) { return }
      // find by matching userInput if it exists
      if (size.userInput) {
        return this.sizeOptions.find(i => i.userInput === size.userInput)
      }
      return this.sizeOptions.find(i => i.label === size.label)
    },
    getSubstrate(substrate) {
      // returns a substrate from list of available substrates
      const substrates = this.substrateOptions
      return substrates.find(i => i.value === substrate?.value)
          || substrates.find(i => i.value === 'print')
          || substrates[0]
    },
    async save(item, action, {onStart} = {}){
      const { workspace } = this.selected
      const data = {
        imagedata: item,
        size: this.selected.size.value,
        substrate: this.selected.substrate.value,
        action: action,
        workspace_id: workspace?.id,
        quantity: this.selected.quantity,
        location: this.selected.location,
      }

      let token      = workspace?.project ?? this.cart?.token
      let project_id = workspace?.project ?? this.cart?.project?.id
      let url        = `${UI_URL}projects/${project_id}`

      // get email address from anon users
      let cart_email
      if (!token && !this.profile) {
        cart_email = await this.getAnonEmail()
      }

      // start progress UI
      if (onStart) { onStart() }

      try {
        // if no cart, create a new cart
        if (!token) {
          this.cart = await this.appApi.post('/api/v2/projects/', {cart_email})
            .then(response => response.data)
          if (!this.profile) {
            this.$store.commit('cart/setGuest', true)
          } else {
            // make cart owned by logged in user
            // TODO: set ownership automatically when creating cart, then remove this
            await this.appApi.post(`/api/v2/projects/${this.cart.token}/migrate-cart/`)
          }
          token      = this.cart?.token
          project_id = this.cart?.project?.id
          url        = `${UI_URL}projects/${project_id}`
          /* global dataLayer */
          dataLayer.push({
            'event': 'cart_create',
            'project_id': project_id,
          })
          /* global salesiqReady */
          salesiqReady.then((salesiq) => {
            salesiq.visitor.customaction('Cart create', {
              url,
              project_id,
            })
          }).catch(() => {})
        }

        // add item to cart
        const response = await this.appApi.post(`/api/v2/projects/${token}/items/`, data)
        const item_id = response.data.id
        dataLayer.push({
          'event': `cart_${action}`, // e.g. cart_add, cart_open
          'item_id': item_id,
          'project_id': project_id,
        })
        salesiqReady.then((salesiq) => {
          salesiq.visitor.customaction(`Cart ${action}`, {
            url,
            project_id,
            item_id,
          })
        }).catch(() => {})

        // report popular
        await http.patch(
          `/content/edit/${item.id}/`,
          {popular: true},
        ).catch(() => {})

        return {response, project_id, token}
      } catch (err) {
        const message = getErrorDetail(err) || err
        this.$alert.simple(`${message}`, {variant: 'danger'})
        throw err
      }
    },
    async add(item) {
      try {
        const {project_id, token} = await this.save(item, 'add', {
          onStart: () => this.busy = 'add',
        })
        await this.onAdded(item, project_id, token)
      } finally {
        this.busy = false
      }
    },
    async onAdded(item, project_id, token) {
      const addedToActiveCart = !this.cart || this.cart?.project?.id === project_id
      if (addedToActiveCart) {
        this.toast(item, '/cart')
        // update cart if it is the current project
        // cart is not updated until async image processing completes, so wait a bit before refreshing
        setTimeout(async () => {
          await this.updateCart(token)
          this.updateWorkspaces()
        }, 3000)
      } else {
        this.toast(item, `/projects/${project_id}`)
      }
    },
    async updateCart(token) {
          this.cart = await this.appApi.get(`/api/v2/projects/${token}/`)
            .then(response => response.data)
          this.$store.dispatch('cart/fetchItems')
    },
    toast(item, to) {
      const image = this.$createElement('img', {attrs: {src: getSrc(item, 'thumb')}, class: 'toast-image mr-2'})
      const message = `"${item.title}" added to ${this.profile ? 'Project' : 'Cart'}`
      this.$bvToast.toast(
        [image, message],
        {to, variant: 'success', noCloseButton: true, solid: true, bodyClass: 'd-flex align-items-center text-reset'}
      )
    },
    async updateWorkspaces() {
      if (!this.selected.workspace && this.$refs.workspaceInput) {
        if (this.user) {
          await this.fetchProfile().catch(() => {})
        }
        this.$refs.workspaceInput.loadWorkspaces()
      }
    },
    async open(item) {
      const { response, project_id, token } = await this.save(item, 'open', {
        onStart: () => {
          this.busy = 'open'
          this.$SmartProgress.start()
        },
      })
        .catch(err => {
          this.busy = false
          this.$SmartProgress.fail()
          throw err
        })
      if (this.foreground) {
        window.location = `${API_URL}art/design/${response.data.id}`
      } else {
        // if a link was clicked during the request, it implies the user no
        // longer wants the pending navigation, so notify instead
        await this.onAdded(item, project_id, token)
        this.busy = false
      }
    },
    onCustomizeClick(event, item) {
      if (event?.altKey) {
        this.$router.push({
          path: '/design-studio',
          query: this.designStudioQuery,
        })
        return
      }
      this.open(item)
    },
    addCustomSize(searchQuery) {
      // Handle when user types a custom size and presses enter

      if (!this.customSizesAllowed) {
        return;
      }

      const { size } = parseDimensionString(searchQuery);

      // Check if we got any valid dimensions
      if (!size.w && !size.h) {
        return;
      }

      // Create the size option (works for both custom and adaptive)
      const aspectRatio = this.item?.max_width / this.item?.max_height;
      const customSize = createSizeOption(size, aspectRatio);

      // Validate dimensions are reasonable
      if (!Object.values(customSize.value).every(isValidDimension)) {
        return;
      }

      // Save to preferences and select
      this.saveCustomSizeToPreferences(customSize);
      this.selected.size = customSize;
    },
    onSizeSelect(selectedOption) {
      // Handle when a size is selected from the dropdown
      if (this.dynamicSizeOptions.includes(selectedOption)) {
        // Save the dynamic size to preferences
        this.saveCustomSizeToPreferences(selectedOption);
      } else if (selectedOption.userInput) {
        // Re-save custom size to put it first in the list of custom sizes
        // This prevents deduplication from eliminating the selected size when multiple
        // adaptive sizes resolve to the same dimensions (e.g. 3x_ and _x3 both become 3x3 on square images)
        this.saveCustomSizeToPreferences(selectedOption);
      }
    },
    onSizeSearchChange(searchQuery) {
      if (!this.customSizesAllowed) { return }
      // Handle dynamic size generation when user types a number
      this.dynamicSizeOptions = generateDynamicSizeOptions(searchQuery, this.item);
      // Update the reactive search query
      this.sizeSearchQuery = searchQuery;
    },
    saveCustomSizeToPreferences(customSize) {
      let customSizes = [customSize, ...this.customSizes];
      // Deduplicate before slicing so we don't lose any sizes due to reordering
      customSizes = uniqBy(customSizes, i => JSON.stringify(i.userInput));
      // Limit the number of custom sizes for usability, the rest of the code is designed to support an arbitrary number
      customSizes = customSizes.slice(0, 1);
      this.$store.commit('preferences/setPreference', { customSizes });
    },
  },
}

function sizesApi(width, height) {
  return getAppApi().get(`/api/v1/availablesizes/?w=${width}&h=${height}`)
}
// Available sizes are based on ratio, so we only need one request per ratio
const sizesApiCached = memoize(sizesApi, (w, h) => {
  const ratio = w / h
  return isNaN(ratio) ? `${w},${h}` : ratio
})

// normalize dimension strings and extract width and height
// "20.333x30.666 Test" -> { normalized: "20.33 x 30.67 Test", size: { w: 20.33, h: 30.67 } }
// "20x" -> { normalized: "20 x ", size: { w: 20, h: null} }
// "x30" -> { normalized: " x 30", size: { w: null, h: 30} }
export function parseDimensionString(input) {
  if (!input || typeof input !== 'string') {
    return { normalized: input, size: { w: null, h: null } };
  }

  // Pattern to match dimension strings with optional prefixes/suffixes
  // Matches: optional prefix, width (with decimals), separator, height (with decimals), optional suffix
  const dimensionPattern = /^(.*?)(\d*\.?\d*)\s*([x*×X]|by)?\s*(\d*\.?\d*)(.*?)$/i;
  const match = input.match(dimensionPattern);

  if (!match) {
    return { normalized: input, size: { w: null, h: null } };
  }

  const [, prefix, widthStr, separatorStr, heightStr, suffix] = match;

  // Parse dimensions, handling empty strings as null
  const w = widthStr ? round(parseFloat(widthStr), 2) : null;
  const separator = separatorStr ? ' x ' : '';
  const h = heightStr ? round(parseFloat(heightStr), 2) : null;

  // Build normalized string with proper spacing
  const normalized = `${prefix}${w ?? ''}${separator}${h ?? ''}${suffix}`;

  return {
    normalized,
    size: { w, h },
  };
}

function isAdaptive(userInput) {
  return userInput && (userInput.w === null || userInput.h === null);
}

export function createSizeOption(userInput, aspectRatio) {
  // Unified function to create size options (both custom and adaptive)
  const w = userInput.w ?? round(userInput.h * aspectRatio, 1);
  const h = userInput.h ?? round(userInput.w / aspectRatio, 1);

  // Determine if it's adaptive (has null values) or custom
  const suffix = isAdaptive(userInput) ? ' (Auto)' : ' (Custom)';

  return {
    label: `${w} x ${h}${suffix}`,
    value: { w, h },
    userInput
  };
}

function isValidDimension(value) {
  return value >= 0.1 && value <= 1000;
}

export function generateDynamicSizeOptions(input, item) {
  if (!input || !item?.max_width || !item?.max_height) return [];

  const { size } = parseDimensionString(input);
  const number = size.w || size.h;
  if (!number || number <= 0) return [];

  const aspectRatio = item.max_width / item.max_height;

  // Generate both width-based and height-based variants
  const options = [
    { w: number, h: null }, // Width-based adaptive
    { w: null, h: number }  // Height-based adaptive
  ];

  // Create size options, ensuring they are valid dimensions
  return options
    .map(userInput => createSizeOption(userInput, aspectRatio))
    .filter(option => Object.values(option.value).every(isValidDimension));
}

function getErrorDetail(err) {
  const message = err?.response?.data?.detail
    || JSON.stringify(err?.response?.data)
  return message && new Error(message)
}
</script>

<style lang="scss" scoped>
.search-option {
  text-align: left;
  & + & {
    margin-top: 0.25rem;
  }
  .title {
    font-size: 0.8em;
  }
}
</style>
